(() => {

class AnnouncementBar extends HTMLElement {
  #timer = undefined;
  #current = 0;
  #slyde = null;
  #slides = null;
  #autoplay = null;

  connectedCallback() {
    const mode = this.getAttribute('mode');

    switch (mode) {
      case 'marquee':
        this.#initMarquee();
        break;
      case 'slideshow':
        this.#initSlideshow();
        break;
      case 'fade':
      case 'slide-down':
        this.#initRotation();
        break;
    }

    this.addEventListener('click', this.#onControlClick);
    this.addEventListener('pointerenter', this.#onPointerEnter);
    this.addEventListener('pointerleave', this.#onPointerLeave);
    document.addEventListener('visibilitychange', this.#onVisibility);
  }

  disconnectedCallback() {
    clearInterval(this.#timer);
    if (this.#slyde) {
      this.#slyde.destroy();
      this.#slyde = null;
    }
    this.#autoplay = null;
    this.removeEventListener('click', this.#onControlClick);
    this.removeEventListener('pointerenter', this.#onPointerEnter);
    this.removeEventListener('pointerleave', this.#onPointerLeave);
    document.removeEventListener('visibilitychange', this.#onVisibility);
  }

  // ---- Marquee ----

  #initMarquee() {
    const content = this.querySelector('.lh-announcement-bar__content');
    const track = this.querySelector('.lh-announcement-bar__track');
    if (!content || !track) return;

    const w = content.offsetWidth;
    if (w === 0) return;

    const copies = Math.max(1, Math.ceil((window.innerWidth * 2) / w));
    for (let i = 0; i < copies; i++) {
      const clone = content.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      track.appendChild(clone);
    }

    track.style.setProperty('--marquee-offset', `-${w}px`);
  }

  // ---- Slideshow (Slyde) ----

  #initSlideshow() {
    const { Slyde, Autoplay } = window.Slyde || {};
    if (!Slyde) return;

    // Slyde sizes slides from its root, so with prev/next controls beside the
    // track the root has to be the track's own box, not the whole bar.
    const root = this.querySelector('[data-announcement-viewport]') || this;
    const wrapper = root.querySelector('.slyde-wrapper');
    if (!wrapper || wrapper.children.length <= 1) return;

    const autoplay = this.hasAttribute('autoplay');
    const delay = this.#ms();
    const modules = autoplay && Autoplay ? [Autoplay] : [];

    requestAnimationFrame(() => {
      this.#slyde = new Slyde(root, {
        loop: true,
        slidesPerView: 1,
        spaceBetween: 0,
        navigation: false,
        pagination: false,
        thumbnails: false,
        video: false,
        freeMode: false,
        autoplay: autoplay ? { delay, pauseOnHover: false } : false,
        modules,
      });

      this.#autoplay = this.#slyde.modules?.find((m) => m.name === 'autoplay') || null;
    });
  }

  // ---- Fade / Slide-down ----

  #initRotation() {
    this.#slides = this.querySelectorAll('.lh-announcement-bar__slide');
    if (this.#slides.length <= 1) return;

    if (this.hasAttribute('autoplay')) {
      this.#timer = setInterval(() => this.#rotate(1), this.#ms());
    }
  }

  #rotate(step) {
    const count = this.#slides.length;
    const prev = this.#current;
    this.#current = (this.#current + step + count) % count;
    const mode = this.getAttribute('mode');

    if (mode === 'slide-down') {
      this.#slides[prev].classList.add('is-exiting');
      this.#slides[prev].classList.remove('is-active');
      setTimeout(() => this.#slides[prev].classList.remove('is-exiting'), 400);
    } else {
      this.#slides[prev].classList.remove('is-active');
    }

    this.#slides[this.#current].classList.remove('is-exiting');
    this.#slides[this.#current].classList.add('is-active');
  }

  #restartRotation() {
    if (!this.hasAttribute('autoplay')) return;
    clearInterval(this.#timer);
    this.#timer = document.hidden ? undefined : setInterval(() => this.#rotate(1), this.#ms());
  }

  // ---- Prev / next controls ----

  #onControlClick = (event) => {
    const control = event.target.closest('[data-announcement-prev], [data-announcement-next]');
    if (!control || !this.contains(control)) return;
    const step = control.hasAttribute('data-announcement-prev') ? -1 : 1;

    if (this.#slyde) {
      // A press during the loop-seam animation would sweep back across every slide.
      if (this.#slyde.slideAnimationID) return;
      if (step < 0) this.#slyde.prev();
      else this.#slyde.next();
      return;
    }

    if (this.#slides && this.#slides.length > 1) {
      this.#rotate(step);
      this.#restartRotation();
    }
  };

  // A tap fires a compatibility mouseenter with no matching leave, so only a real
  // mouse pauses autoplay; on touch it would otherwise stop until the next tap elsewhere.
  #onPointerEnter = (event) => {
    if (event.pointerType !== 'mouse') return;
    this.#autoplay?.pause();
  };

  #onPointerLeave = (event) => {
    if (event.pointerType !== 'mouse') return;
    this.#autoplay?.resume();
  };

  // ---- Helpers ----

  #ms() {
    return (parseInt(this.getAttribute('interval'), 10) || 5) * 1000;
  }

  #onVisibility = () => {
    if (!this.hasAttribute('autoplay')) return;
    const mode = this.getAttribute('mode');
    if (mode === 'marquee' || mode === 'slideshow') return;

    if (document.hidden) {
      clearInterval(this.#timer);
      this.#timer = undefined;
    } else if (!this.#timer) {
      this.#timer = setInterval(() => this.#rotate(1), this.#ms());
    }
  };
}

if (!customElements.get('lh-announcement-bar')) {
  customElements.define('lh-announcement-bar', AnnouncementBar);
}

})();
